import {
  useCallback,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import BrandHeader from "../../shared/layouts/BrandHeader";
import BrandFooter from "../../shared/layouts/BrandFooter";
import {
  designTokens,
  designClasses,
} from "../../shared/styles/designTokens";
import CollapsibleSection from "../../shared/components/CollapsibleSection";
import useApiData from "../../hooks/useApiData";
import registrationService from "../../services/registrationService";
import authService from "../../services/authService";
import {
  calculateProfileCompletion,
} from "../../shared/utils/profileCompletion";

import useRegistrationAvailability from "./hooks/useRegistrationAvailability";

import {
  preferredAgeRangeConfig,
  preferredHeightRangeConfig,
  preferredIncomeRangeConfig,
} from "../../shared/config/profileOptions";

import BasicDetailsSection from "./components/BasicDetailsSection";
import ContactAccountSection from "./components/ContactAccountSection";
import PersonalDetailsSection from "./components/PersonalDetailsSection";
import CareerEducationSection from "./components/CareerEducationSection";
import AboutPhotoSection from "./components/AboutPhotoSection";
import FamilyDetailsSection from "./components/FamilyDetailsSection";
import ContactAddressReferencesSection from "./components/ContactAddressReferencesSection";
import PartnerPreferencesSection from "./components/PartnerPreferencesSection";
import ReviewCompleteSection from "./components/ReviewCompleteSection";
import EmailVerificationGate from "./components/EmailVerificationGate";
import {
  calculateCurrentAgeText,
  deriveProfileFor,
  generateProfileId,
  getLockedGender,
  heightRangeInchesToCm,
  normalizeSelectedValues,
} from "./utils/registrationBusinessRules";

import {
  getFirstValidationMessage,
  validateRegistration,
} from "./validation/registrationValidation";

const initialFormData = {
  // Basic
  profileCreatedFor: "",
  profileFor: "",
  profileId: "",
  name: "",
  gender: "",
  dob: "",
  motherTongue: "",
  gotra: "",
  currentLocation: "",

  // Contact, account & location
  phoneCountryCode: "+91",
  phoneNumber: "",
  phone: "",
  alternatePhone: "",
  guardianPhone: "",
  email: "",
  password: "",
  confirmPassword: "",
  currentLocationState: "",
  currentLocationCountry: "",
  nativePlaceState: "",
  nativePlaceCountry: "",
  communicationAddress: "",
  residenceAddress: "",

  // Education, career & personal
  marriedStatus: "",
  height: "",
  heightFeet: "",
  heightInches: "",
  nativePlace: "",
  education: "",
  profession: "",
  designation: "",
  currentCompany: "",
  countryLivingIn: "",
  annualIncome: "",
  workingStatus: "",
  profileCategory: "",
  shareDetailsOnPlatform: "Yes",
  diet: [],
  hobbies: [],
  aboutBrideGroom: "",

  // Community & horoscope
  guruMatha: "",
  timeOfBirth: "",
  subCaste: "",
  placeOfBirth: "",
  placeOfBirthState: "",
  placeOfBirthCountry: "",
  rashi: "",
  nakshatra: "",
  charanaPada: "",

  // Family & references
  fatherName: "",
  fatherProfession: "",
  motherName: "",
  motherProfession: "",
  noOfBrothers: "",
  noOfSisters: "",
  familyStatus: "",
  familyType: "",
  familyValues: "",
  reference1Name: "",
  reference1Phone: "",
  reference2Name: "",
  reference2Phone: "",
  howDidYouKnow: "",

  // Photo & preferences
  photo: null,
  expectations: "",
  ageRange:
    preferredAgeRangeConfig.defaultValue,
  heightRange:
    preferredHeightRangeConfig.defaultValue,
  preferredIncomeRange:
    preferredIncomeRangeConfig.defaultValue,
  preferredMotherTongues: [],
  preferredGotras: [],
  preferredMaritalStatus: "",
  preferredEducation: [],
  preferredCities: [],
  preferredBrideGroomCategory: "",
  preferredSubCastes: [],
  preferredGuruMathas: [],
  preferredNakshatras: [],
  preferredRashis: [],
  preferredNativeOrigins: [],
  preferredCountries: [],
  preferredProfessions: [],
  preferredDiet: [],
  preferredHobbies: [],

  // Final declaration
  declarationAccepted: false,
};

const SinglePageRegistration = () => {
  const navigate = useNavigate();

const [formData, setFormData] =
  useState(initialFormData);

  const [
    registrationVerificationToken,
    setRegistrationVerificationToken,
  ] = useState("");

  const [emailVerified, setEmailVerified] =
    useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [openSection, setOpenSection] = useState("basic");
  const {
  availability,
  checking: availabilityChecking,
  clearEmailAvailability,
  clearPhoneAvailability,
  checkEmailAvailability,
  checkPhoneAvailability,
  checkAccountAvailability,
} = useRegistrationAvailability();
  const toggleSection = (sectionName) => {
    setOpenSection((current) =>
      current === sectionName ? null : sectionName
    );
  };
  const {
    isLoading: lookupLoading,
    gotraOptions,
    rashiOptions,
    nakshatraOptions,
    searchMotherTongues,
    searchPlaces,
    searchGuruMatha,
    searchEducation,
    searchProfessions,
    searchDesignations,
  } = useApiData();

  
 
  const {
    percentage: completion,
  } = calculateProfileCompletion(formData);

  const basicComplete = Boolean(
    formData.profileCreatedFor &&
      formData.name &&
      formData.gender &&
      formData.motherTongue &&
      formData.currentLocationCountry
  );

  const accountComplete = Boolean(
    formData.phone &&
      formData.email &&
      formData.dob &&
      formData.password &&
      formData.confirmPassword
  );

  const personalComplete = Boolean(
    formData.marriedStatus &&
      formData.heightFeet &&
      formData.heightInches !== "" &&
      formData.profileCategory &&
      formData.gotra &&
      formData.rashi &&
      formData.nakshatra &&
      formData.charanaPada &&
      formData.subCaste &&
      formData.guruMatha
  );

  const careerComplete = Boolean(
    formData.education &&
      formData.profession &&
      formData.workingStatus &&
      formData.annualIncome
  );

  const aboutPhotoComplete = Boolean(
    String(
      formData.aboutBrideGroom || ""
    ).trim().length >= 50
  );

  const familyComplete = Boolean(
    formData.fatherName &&
      formData.fatherProfession &&
      formData.motherName &&
      formData.motherProfession &&
      formData.noOfBrothers &&
      formData.noOfSisters &&
      formData.familyStatus &&
      formData.familyType &&
      formData.familyValues
  );

  const contactReferenceComplete = Boolean(
    formData.reference1Name &&
      formData.reference1Phone
  );

  const preferencesComplete = Boolean(
    formData.preferredMotherTongues?.length > 0 ||
      formData.preferredGotras?.length > 0 ||
      formData.preferredMaritalStatus ||
      formData.preferredEducation?.length > 0 ||
      formData.preferredCities?.length > 0
  );

  const updateField = (event) => {
    const {
      name,
      value,
      type,
      checked,
      files,
    } = event.target;

    if (
      name === "email" &&
      emailVerified
    ) {
      setRegistrationVerificationToken("");
      setEmailVerified(false);
    }

    setFormData((current) => {
      const next = {
        ...current,
      };

      const nextValue =
        type === "checkbox"
          ? checked
          : type === "file"
          ? files?.[0] || null
          : value;

      next[name] = nextValue;

      if (name === "email") {
  clearEmailAvailability();
}

      if (
        name ===
        "profileCreatedFor"
      ) {
        const lockedGender =
          getLockedGender(
            nextValue
          );

        if (lockedGender) {
          next.gender =
            lockedGender;

          next.profileFor =
            deriveProfileFor(
              lockedGender
            );
        }
      }

      if (name === "gender") {
        next.profileFor =
          deriveProfileFor(
            nextValue
          );
      }
    
      return next;
    });
  };

  const setFieldValue = useCallback(
    (name, value) => {
      setFormData((current) => {
        if (
          current[name] === value
        ) {
          return current;
        }

        return {
          ...current,
          [name]: value,
        };
      });
    },
    []
  );

  const handlePhoneChange = (event) => {
    const { name } = event.target;
    clearPhoneAvailability();

    const value =
      name === "phoneNumber"
        ? event.target.value.replace(/\D/g, "")
        : event.target.value;

    setFormData((current) => {
      const next = {
        ...current,
        [name]: value,
      };

      const countryCode =
        name === "phoneCountryCode"
          ? value
          : next.phoneCountryCode || "+91";

      const phoneNumber =
        name === "phoneNumber"
          ? value
          : next.phoneNumber || "";

      next.phone = phoneNumber
  ? `${countryCode}${phoneNumber}`
  : "";


      return next;
    });
  };

  const handleEmailVerified = ({
    name,
    email,
    verificationToken,
  }) => {
    setFormData((current) => ({
      ...current,
      name,
      email,
    }));

    clearEmailAvailability();

    setRegistrationVerificationToken(
      verificationToken
    );

    setEmailVerified(true);
    setOpenSection("basic");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    let persistedProfileId = "";

    const profileId =
  formData.profileId ||
  generateProfileId(
    formData.name,
    formData.phone
  );

const submissionData = {
  ...formData,
  profileId,
};

const backendProfileData = {
  profileId:
    submissionData.profileId,

  name:
    submissionData.name,

  profileCreatedFor:
    submissionData.profileCreatedFor,

  profileFor:
    submissionData.profileFor,

  gender:
    submissionData.gender,

  motherTongue:
    submissionData.motherTongue,

  currentLocation:
    submissionData.currentLocation,

  profileStatus: "DRAFT",

  marriedStatus:
    submissionData.marriedStatus,

  gotra:
    submissionData.gotra,

  dob:
    submissionData.dob,

  currentAge:
    calculateCurrentAgeText(
      submissionData.dob
    ),

  height:
    submissionData.height,

  heightFeet:
    submissionData.heightFeet,

  heightInches:
    submissionData.heightInches,

  nativePlace:
    submissionData.nativePlace,

  email:
    submissionData.email,

  phone:
    submissionData.phone,

  aboutBrideGroom:
    submissionData.aboutBrideGroom,

  workingStatus:
    submissionData.workingStatus,

  education:
    submissionData.education,

  profession:
    submissionData.profession,

  designation:
    submissionData.designation,

  currentCompany:
    submissionData.currentCompany,

  countryLivingIn:
    submissionData.countryLivingIn,

  annualIncome:
    submissionData.annualIncome,

  profileCategory:
    submissionData.profileCategory,

  shareDetailsOnPlatform:
    submissionData.shareDetailsOnPlatform,

  diet:
    submissionData.diet,

  hobbies:
    submissionData.hobbies,

  alternatePhone:
    submissionData.alternatePhone,

  guardianPhone:
    submissionData.guardianPhone,

  communicationAddress:
    submissionData.communicationAddress,

  residenceAddress:
    submissionData.residenceAddress,

  currentLocationState:
    submissionData.currentLocationState,

  currentLocationCountry:
    submissionData.currentLocationCountry,

  nativePlaceState:
    submissionData.nativePlaceState,

  nativePlaceCountry:
    submissionData.nativePlaceCountry,

  guruMatha:
    submissionData.guruMatha,

  timeOfBirth:
    submissionData.timeOfBirth,

  subCaste:
    submissionData.subCaste,

  placeOfBirth:
    submissionData.placeOfBirth,

  placeOfBirthState:
    submissionData.placeOfBirthState,

  placeOfBirthCountry:
    submissionData.placeOfBirthCountry,

  rashi:
    submissionData.rashi,

  nakshatra:
    submissionData.nakshatra,

  charanaPada:
    submissionData.charanaPada,

  fatherName:
    submissionData.fatherName,

  fatherProfession:
    submissionData.fatherProfession,

  motherName:
    submissionData.motherName,

  motherProfession:
    submissionData.motherProfession,

  noOfBrothers:
    submissionData.noOfBrothers,

  noOfSisters:
    submissionData.noOfSisters,

  familyStatus:
    submissionData.familyStatus,

  familyType:
    submissionData.familyType,

  familyValues:
    submissionData.familyValues,

  reference1Name:
    submissionData.reference1Name,

  reference1Phone:
    submissionData.reference1Phone,

  reference2Name:
    submissionData.reference2Name,

  reference2Phone:
    submissionData.reference2Phone,

  howDidYouKnow:
    submissionData.howDidYouKnow,

  expectations:
    submissionData.expectations,

  ageRange:
    submissionData.ageRange,

  heightRange:
    heightRangeInchesToCm(
      submissionData.heightRange
    ),

  preferredIncomeRange:
    submissionData.preferredIncomeRange,

  preferredEducation:
    normalizeSelectedValues(
      submissionData.preferredEducation
    ),

  preferredMotherTongues:
    normalizeSelectedValues(
      submissionData.preferredMotherTongues
    ),

  preferredMaritalStatus:
    submissionData.preferredMaritalStatus,

  preferredGotras:
    normalizeSelectedValues(
      submissionData.preferredGotras
    ),

  preferredCities:
    normalizeSelectedValues(
      submissionData.preferredCities
    ),

  preferredBrideGroomCategory:
    submissionData.preferredBrideGroomCategory,

  preferredSubCastes:
    normalizeSelectedValues(
      submissionData.preferredSubCastes
    ),

  preferredGuruMathas:
    normalizeSelectedValues(
      submissionData.preferredGuruMathas
    ),

  preferredNakshatras:
    normalizeSelectedValues(
      submissionData.preferredNakshatras
    ),

  preferredRashis:
    normalizeSelectedValues(
      submissionData.preferredRashis
    ),

  preferredNativeOrigins:
    normalizeSelectedValues(
      submissionData.preferredNativeOrigins
    ),

  preferredCountries:
    normalizeSelectedValues(
      submissionData.preferredCountries
    ),

  preferredProfessions:
    normalizeSelectedValues(
      submissionData.preferredProfessions
    ),

  preferredDiet:
    normalizeSelectedValues(
      submissionData.preferredDiet
    ),

  preferredHobbies:
    normalizeSelectedValues(
      submissionData.preferredHobbies
    ),
};

const validationErrors =
  validateRegistration(
    submissionData
  );

  if (
    Object.keys(
      validationErrors
    ).length > 0
  ) {
    const message =
      getFirstValidationMessage(
        validationErrors
      );

    window.alert(message);

    const firstError =
      Object.keys(
        validationErrors
      )[0];

    if (
      [
        "profileCreatedFor",
        "name",
        "gender",
        "motherTongue",
        "currentLocationCountry",
      ].includes(firstError)
    ) {
      setOpenSection("basic");
    } else if (
      [
        "phoneNumber",
        "profileId",
        "email",
        "dob",
        "password",
        "confirmPassword",
      ].includes(firstError)
    ) {
      setOpenSection("contact");
    } else if (
      [
        "marriedStatus",
        "heightFeet",
        "heightInches",
        "profileCategory",
        "gotra",
        "rashi",
        "nakshatra",
        "charanaPada",
        "subCaste",
        "guruMatha",
      ].includes(firstError)
    ) {
      setOpenSection("personal");
    } else if (
      [
        "education",
        "profession",
        "workingStatus",
        "annualIncome",
      ].includes(firstError)
    ) {
      setOpenSection("career");
    } else if (
      firstError ===
      "aboutBrideGroom"
    ) {
      setOpenSection("aboutPhoto");
    } else if (
      [
        "fatherName",
        "fatherProfession",
        "motherName",
        "motherProfession",
        "noOfBrothers",
        "noOfSisters",
        "familyStatus",
        "familyType",
        "familyValues",
      ].includes(firstError)
    ) {
      setOpenSection("family");
    } else if (
      [
        "reference1Name",
        "reference1Phone",
      ].includes(firstError)
    ) {
      setOpenSection("contactAddress");
    } else {
      setOpenSection("review");
    }

    return;
  }

    if (!registrationVerificationToken) {
      window.alert(
        "Please verify your email before completing registration."
      );
      return;
    }

    setSubmitting(true);

    try {
      const preflight =
        await checkAccountAvailability({
          email: submissionData.email,
          phoneCountryCode:
            submissionData.phoneCountryCode,
          phoneNumber:
            submissionData.phoneNumber,
        });

      if (preflight?.email?.exists) {
        window.alert(
          'This email is already registered. Please login with this email and use "Forgot Password" if needed.'
        );

        setOpenSection("account");
        return;
      }

      if (preflight?.phone?.exists) {
        window.alert(
          "This phone number is already registered. Please login with this number."
        );

        setOpenSection("account");
        return;
      }

      const profileExists =
        await registrationService.checkProfileExists(
          submissionData.profileId
        );

      if (profileExists) {
        window.alert(
          "The generated Profile ID already exists. Please try again."
        );

        setOpenSection("account");
        return;
      }

const profileResponse =
  await registrationService.createProfile({
    profileData: {
      ...backendProfileData,
      profileStatus: "DRAFT",
    },

    userLoginData: {
      user_id:
        submissionData.email,
      password:
        submissionData.password,
      role: "USER",
      is_active: "Yes",
      notes: "",
    },

    registrationVerificationToken,
  });

const createdProfileId =
  profileResponse?.profileId;

  persistedProfileId =
  createdProfileId;

if (!createdProfileId) {
  throw new Error(
    "Profile creation succeeded without returning a Profile ID."
  );
}


let photoUploadFailed = false;

if (submissionData.photo) {
  try {
    await registrationService.uploadProfilePhoto({
      profileId:
        createdProfileId,

      email:
        submissionData.email,

      photo:
        submissionData.photo,

      isDefault: true,
    });
  } catch (photoError) {
    photoUploadFailed = true;

    console.error(
      "Profile photo upload failed:",
      photoError
    );
  }
}

const loginResponse =
  await authService.login({
    userId:
      submissionData.email,

    password:
      submissionData.password,
  });

const token =
  loginResponse?.token;

if (!token) {
  throw new Error(
    "Profile was created, but automatic login could not be completed."
  );
}

sessionStorage.setItem(
  "token",
  token
);

sessionStorage.setItem(
  "isLoggedIn",
  "true"
);

sessionStorage.setItem(
  "profileId",
  createdProfileId
);

sessionStorage.setItem(
  "profileStatus",
  "DRAFT"
);

sessionStorage.setItem(
  "userEmail",
  submissionData.email
);

let role = (
  loginResponse?.user?.role ||
  loginResponse?.role ||
  ""
).toString();

try {
  if (
    !role &&
    token
  ) {
    const payloadBase64 =
      token.split(".")[1];

    const payloadJson =
      JSON.parse(
        atob(
          payloadBase64
        )
      );

    role = (
      payloadJson?.role ||
      ""
    ).toString();
  }
} catch (roleError) {
  console.warn(
    "Unable to resolve user role from login response:",
    roleError
  );
}

if (role) {
  sessionStorage.setItem(
    "userRole",
    role
  );
}


setFormData((current) => ({
  ...current,
  profileId:
    createdProfileId,
}));

const successLines = [
  "Profile created successfully.",
  "",
  `Profile ID: ${createdProfileId}`,
  `Name: ${submissionData.name}`,
  `Login ID: ${submissionData.email}`,
  "Password: Use the password entered during registration.",
];

if (photoUploadFailed) {
  successLines.push(
    "",
    "Your profile was created successfully, but the profile photo could not be uploaded. You can upload it later from My Profile."
  );
}

window.alert(
  successLines.join("\n")
);
navigate(
  "/dashboard",
  {
    replace: true,
  }
);

    } catch (error) {
      console.error(
  "Registration failed:",
  error
);

      const serverMessage =
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.response?.data?.details ||
  error?.message;

if (persistedProfileId) {
  window.alert(
    [
      "Your profile has been created.",
      "",
      `Profile ID: ${persistedProfileId}`,
      `Login ID: ${submissionData.email}`,
      "",
      "Automatic sign-in could not be completed.",
      "Please use the Member Login screen with the password you entered during registration.",
    ].join("\n")
  );
} else {
  window.alert(
    serverMessage
      ? `Registration could not be completed: ${serverMessage}`
      : "Registration could not be completed. Please try again."
  );
}
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
  className="flex min-h-screen flex-col"
  style={{ backgroundColor: designTokens.colors.page }}
>
      <BrandHeader />

      <main className="flex-1">
        {!emailVerified ? (
          <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
            <div className="mb-8 text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-700">
                Kalyana Sakha
              </p>

              <h1
                className="mt-2 text-3xl font-semibold tracking-tight md:text-4xl"
                style={{ color: designTokens.colors.primary }}
              >
                Create Your Profile
              </h1>

              <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
                Start by verifying your email address. Your full profile form will open after verification.
              </p>
            </div>

            <EmailVerificationGate
              initialName={formData.name}
              initialEmail={formData.email}
              onVerified={handleEmailVerified}
            />
          </div>
        ) : (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
          <div className="mb-8 text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-amber-700">
              Kalyana Sakha
            </p>

            <h1
  className="mt-2 text-3xl font-semibold tracking-tight md:text-4xl"
  style={{ color: designTokens.colors.primary }}
>
              Create Your Profile
            </h1>

            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
              Tell us the essential details needed to create your profile and
              help you discover meaningful matches.
            </p>
          </div>

          <div className="mb-6 rounded-2xl border border-amber-200 bg-white px-5 py-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between gap-4">
              <span className="text-sm font-medium text-slate-700">
                Registration progress
              </span>

              <span
  className="text-sm font-semibold"
  style={{ color: designTokens.colors.primary }}
>
                {completion}%
              </span>
            </div>

            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-amber-500 transition-all duration-300"
                style={{ width: `${completion}%` }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <CollapsibleSection
              number="1"
              title="Basic Information"
              description="Legacy registration basics: profile owner, name, mother tongue, residing country/city and gender."
              open={openSection === "basic"}
              completed={basicComplete}
              onToggle={() => toggleSection("basic")}
            >
              <BasicDetailsSection
                formData={formData}
                updateField={updateField}
                setFieldValue={setFieldValue}
                searchMotherTongues={searchMotherTongues}
                searchPlaces={searchPlaces}
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="2"
              title="Contact Details"
              description="Verified email, phone, date of birth and secure account password."
              open={openSection === "contact"}
              completed={accountComplete}
              onToggle={() => toggleSection("contact")}
            >
              <ContactAccountSection
                formData={formData}
                updateField={updateField}
                handlePhoneChange={handlePhoneChange}
                availability={availability}
                availabilityChecking={availabilityChecking}
                onPhoneBlur={() =>
                  checkPhoneAvailability(
                    formData.phoneCountryCode,
                    formData.phoneNumber
                  )
                }
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="3"
              title="Personal Details"
              description="Legacy personal and horoscope details with structured place-of-birth selection."
              open={openSection === "personal"}
              completed={personalComplete}
              onToggle={() => toggleSection("personal")}
            >
              <PersonalDetailsSection
                formData={formData}
                updateField={updateField}
                setFieldValue={setFieldValue}
                gotraOptions={gotraOptions}
                rashiOptions={rashiOptions}
                nakshatraOptions={nakshatraOptions}
                searchGuruMatha={searchGuruMatha}
                searchPlaces={searchPlaces}
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="4"
              title="Education & Job Details"
              description="Education, profession, designation, company, work status, country and annual income."
              open={openSection === "career"}
              completed={careerComplete}
              onToggle={() => toggleSection("career")}
            >
              <CareerEducationSection
                formData={formData}
                updateField={updateField}
                setFieldValue={setFieldValue}
                searchEducation={searchEducation}
                searchProfessions={searchProfessions}
                searchDesignations={searchDesignations}
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="5"
              title="About Yourself & Photo"
              description="Personal introduction and profile photo."
              open={openSection === "aboutPhoto"}
              completed={aboutPhotoComplete}
              onToggle={() => toggleSection("aboutPhoto")}
            >
              <AboutPhotoSection
                formData={formData}
                updateField={updateField}
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="6"
              title="Family Details"
              description="Legacy family fields with profession lookups."
              open={openSection === "family"}
              completed={familyComplete}
              onToggle={() => toggleSection("family")}
            >
              <FamilyDetailsSection
                formData={formData}
                updateField={updateField}
                setFieldValue={setFieldValue}
                searchProfessions={searchProfessions}
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="7"
              title="Contact, Address & References"
              description="Additional contacts, structured communication/residence addresses and trusted references."
              open={openSection === "contactAddress"}
              completed={contactReferenceComplete}
              onToggle={() => toggleSection("contactAddress")}
            >
              <ContactAddressReferencesSection
                formData={formData}
                setFieldValue={setFieldValue}
                updateField={updateField}
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="8"
              title="Partner Preferences"
              description="Legacy partner-preference sliders, cultural choices, geography, profession and hobbies."
              open={openSection === "preferences"}
              completed={preferencesComplete}
              onToggle={() => toggleSection("preferences")}
            >
              <PartnerPreferencesSection
                formData={formData}
                updateField={updateField}
                setFieldValue={setFieldValue}
                gotraOptions={gotraOptions}
                rashiOptions={rashiOptions}
                nakshatraOptions={nakshatraOptions}
                searchMotherTongues={searchMotherTongues}
                searchEducation={searchEducation}
                searchProfessions={searchProfessions}
                searchGuruMatha={searchGuruMatha}
                searchPlaces={searchPlaces}
              />
            </CollapsibleSection>

            <CollapsibleSection
              number="9"
              title="Review & Complete"
              description="Review the complete profile and accept the declaration before profile creation."
              open={openSection === "review"}
              completed={formData.declarationAccepted}
              onToggle={() => toggleSection("review")}
            >
              <ReviewCompleteSection
                formData={formData}
                updateField={updateField}
              />
            </CollapsibleSection>

            <div className="flex flex-col-reverse items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row">
              <p className="text-sm text-slate-500">
                Required fields must be completed now. Optional profile enrichment can still be updated later from My Profile.
              </p>

              <button
                type="submit"
                disabled={submitting}
                className={`w-full rounded-xl px-7 py-3 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto ${designClasses.primaryButton}`}
              >
                {submitting ? "Creating Profile..." : "Create My Profile"}
              </button>
            </div>
          </form>
        </div>
        )}
      </main>

      <BrandFooter />
    </div>
  );
};

export default SinglePageRegistration;